import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { PackageService } from 'src/app/private/services/package.service';
import { ClientService } from 'src/app/private/services/client.service';

export interface PackageFeature {
  key: string;
  label?: string;
  enabled: boolean;
  isLimit: boolean;
  value?: number;  
  limit?: number;  
}

@Component({
  selector: 'app-package-form',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './package-form.component.html',
  styleUrls: ['./package-form.component.scss']
})
export class PackageFormComponent implements OnInit {

  @Input() packageData: any = null;
  @Input() visible: boolean = false;
  @Input() featuresList: any = {};   // <-- FULL RESPONSE from API

  @Output() close = new EventEmitter<void>();
  @Output() reload = new EventEmitter<void>();

  packageForm!: FormGroup;
  isLoading = false;
  showJson = false;

  /** Processed feature lists */
  fixedFeatureOptions: any[] = [];
  limitFeatureOptions: any[] = [];

  /** Internal structure storing both feature types */
  packageFeatures: PackageFeature[] = [];

  /** Client filter */
  clients: { label: string; value: string }[] = [];
  selectedClientFilter: string | null = null;

  billingCycles = [
    { label: 'Monthly', value: 'Monthly' },
    { label: 'Yearly', value: 'Yearly' },
  ];

  typeOptions = [
    { label: 'CRM', value: 'crm' },
    { label: 'Lite CRM', value: 'lite-crm' },
    { label: 'Marketing Automation', value: 'marketing-automation' },
    { label: 'Support CRM', value: 'support-crm' },
    { label: 'Enterprise CRM', value: 'enterprise-crm' },
  ];

  visibilityOptions = [
    { label: 'PUBLIC', value: 'PUBLIC' },
    { label: 'PRIVATE', value: 'PRIVATE' },
    { label: 'HIDDEN', value: 'HIDDEN' }
  ];

  constructor(
    private fb: FormBuilder,
    private packageService: PackageService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private clientService: ClientService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.processFeatureOptions();
    this.loadAllClients();

    if (this.packageData) this.populateForm();
    else this.resetToDefaults();
  }

  loadAllClients(): void {
    const payload = {
      page: 0,
      limit: 500,
      where: {},
      order: { col: '"client"."name"', dir: 'ASC' }
    };
    this.clientService.getAllClients(payload).subscribe({
      next: (res: any) => {
        const rows = res.rows || res.data || [];
        this.clients = rows.map((c: any) => ({
          label: c.name,
          value: c.client_id || c.id
        }));
      },
      error: () => {}
    });
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   if (changes['featuresList']) {
  //     this.processFeatureOptions();
  //   }

  //   if (changes['visible'] && this.visible) {
  //     if (this.packageData) this.populateForm();
  //     else this.resetToDefaults();
  //   }
  // }

  /** Create Angular form controls */
  initForm(): void {
    
    this.packageForm = this.fb.group({
      package_name: ['', Validators.required],
      type: ['', Validators.required],
      price: [0, [Validators.required, Validators.min(0)]],
      billing_cycle: ['Monthly', Validators.required],
      validity: [30, [Validators.required, Validators.min(1)]],
      description: [''],
      status: ['Active', Validators.required],
      sort_order: [0, [Validators.required, Validators.min(0)]],
      is_recommended: [false],
      is_trial: [false],
      visibility: ['Public', Validators.required]
    });
  }

  /** Separate fixed + limit features for dropdown options - ONLY PARENT FEATURES */
  processFeatureOptions() {
    // Filter only parent features (modules - features without parent_feature_key)
    this.fixedFeatureOptions = (this.featuresList?.fixed || []).filter(
      (feature: any) => !feature.parent_feature_key
    );
    
    this.limitFeatureOptions = (this.featuresList?.limit || []).filter(
      (feature: any) => !feature.parent_feature_key
    );
    
    console.log('Features List:', this.featuresList);
    console.log('Fixed Feature Options (Parents Only):', this.fixedFeatureOptions);
    console.log('Limit Feature Options (Parents Only):', this.limitFeatureOptions);
  }

  /** Default fields for new package */
  resetToDefaults(): void {
    this.packageFeatures = [];
  }


  updateValidityBasedOnCycle(event: any) {
    console.log(event)
  let days = 30; // default
  let cycle = event.value
  switch (cycle) {
    case 'Monthly':
      days = 30;
      break;
    case 'Quarterly':
      days = 90;
      break;
    case 'Yearly':
      days = 365;
      break;
  }

  this.packageForm.patchValue({ validity: days }, { emitEvent: false });
}
formatKey(key: string): string {
  if (!key) return '';

  return key
    .replace(/_/g, " ")        // replace underscores with spaces
    .toLowerCase()             // convert to lowercase
    .replace(/\b\w/g, c => c.toUpperCase());  // capitalize words
}

getLimitLabel(key: string): string {
  if (!key) return '';
  const limit = this.limitFeatureOptions.find(f => f.feature_key === key);
  return limit?.label || this.formatKey(key);
}

getFeatureLabel(key: string): string {
  if (!key) return '';
  const feature = this.fixedFeatureOptions.find(f => f.feature_key === key);
  return feature?.label || this.formatKey(key);
}

/** Get available limit options excluding already added ones */
getAvailableLimitOptions(currentLimit?: PackageFeature): any[] {
  const addedKeys = this.packageFeatures
    .filter(f => f.isLimit && f !== currentLimit)
    .map(f => f.key);
  
  return this.limitFeatureOptions.filter(
    option => !addedKeys.includes(option.feature_key)
  );
}

/** Get available feature options excluding already added ones */
getAvailableFeatureOptions(currentFeature?: PackageFeature): any[] {
  const addedKeys = this.packageFeatures
    .filter(f => !f.isLimit && f !== currentFeature)
    .map(f => f.key);
  
  return this.fixedFeatureOptions.filter(
    option => !addedKeys.includes(option.feature_key)
  );
}
  /** Load an existing package for edit */
  populateForm(): void {
    if (!this.packageData) return;

    this.packageForm.patchValue({
      package_name: this.packageData.package_name,
      type: this.packageData.type,
      price: this.packageData.price?.base || this.packageData.price || 0,
      billing_cycle: this.packageData.billing_cycle,
      validity: this.packageData.validity,
      description: this.packageData.description,
      status: this.packageData.status,
      sort_order: this.packageData.sort_order || 0,
      is_recommended: this.packageData.is_recommended || false,
      is_trial: this.packageData.is_trial || false,
      visibility: this.packageData.visibility || 'Public'
    });

    // Pre-select the client in the dropdown
    this.selectedClientFilter = this.packageData.client_id || null;

    const limits = this.packageData?.features?.limits || [];
    const fixed = this.packageData?.features?.fixed || [];

    this.packageFeatures = [...limits, ...fixed];
  }

  /** LIMITS SECTION */
  addLimit() {
    if (!this.limitFeatureOptions.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Credits Available',
        detail: 'No credits options are available to add'
      });
      return;
    }

    // Get list of already added limit keys
    const addedLimitKeys = this.packageFeatures
      .filter(f => f.isLimit)
      .map(f => f.key);

    // Find first available limit that hasn't been added yet
    const availableLimit = this.limitFeatureOptions.find(
      option => !addedLimitKeys.includes(option.feature_key)
    );

    if (!availableLimit) {
      this.messageService.add({
        severity: 'info',
        summary: 'All Credits Added',
        detail: 'All available credits have already been added to this package'
      });
      return;
    }

    this.packageFeatures.push({
      key: availableLimit.feature_key,
      label: availableLimit.label,
      enabled: true,
      isLimit: true,
      value: 0
    });

    this.cdr.detectChanges();
  }

  /** FEATURES SECTION */
  addFeature() {
    if (!this.fixedFeatureOptions.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Features Available',
        detail: 'No feature options are available to add'
      });
      return;
    }

    // Get list of already added feature keys
    const addedFeatureKeys = this.packageFeatures
      .filter(f => !f.isLimit)
      .map(f => f.key);

    // Find first available feature that hasn't been added yet
    const availableFeature = this.fixedFeatureOptions.find(
      option => !addedFeatureKeys.includes(option.feature_key)
    );

    if (!availableFeature) {
      this.messageService.add({
        severity: 'info',
        summary: 'All Features Added',
        detail: 'All available features have already been added to this package'
      });
      return;
    }

    this.packageFeatures.push({
      key: availableFeature.feature_key,
      label: availableFeature.label,
      enabled: true,
      isLimit: false
    });

    this.cdr.detectChanges();
  }

  removeFeature(item: PackageFeature) {
    this.packageFeatures = this.packageFeatures.filter(f => f !== item);
  }

  onLimitChange(event: any, limit: PackageFeature) {
    if (!event.value) {
      limit.label = '';
      return;
    }

    const selectedLimit = this.limitFeatureOptions.find(f => f.feature_key === event.value);
    if (selectedLimit) {
      limit.label = selectedLimit.label;
    }
  }

  onFeatureChange(event: any, feature: PackageFeature) {
    if (!event.value) {
      feature.label = '';
      return;
    }

    const selectedFeature = this.fixedFeatureOptions.find(f => f.feature_key === event.value);
    if (selectedFeature) {
      feature.label = selectedFeature.label;
    }
  }

  /** Filters */
  getLimits() {
    return this.packageFeatures.filter(f => f.isLimit);
  }

  getFeatures() {
    return this.packageFeatures.filter(f => !f.isLimit);
  }

  /** JSON Preview for UI */
  getJsonPreview() {
    return {
      limits: this.getLimits(),
      fixed: this.getFeatures()
    };
  }

  /** Submit form */
  submitForm(): void {
    if (this.packageForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid Form',
        detail: 'Please fill all required fields'
      });
      return;
    }

    const payload: any = {
      ...this.packageForm.value,
      features: this.getJsonPreview(),
      is_customized: false,
      client_id: this.selectedClientFilter || null
    };

    this.isLoading = true;

    if (this.packageData?.package_id) {
      payload['package_id'] = this.packageData.package_id;
      this.updatePackage(payload);
    } else {
      this.createPackage(payload);
    }
  }

  createPackage(payload: any) {
    this.packageService.createPackage(payload).subscribe({
      next: () => this.successMsg("Package created successfully"),
      error: () => this.errorMsg(),
      complete: () => (this.isLoading = false)
    });
  }

  updatePackage(payload: any) {
    this.packageService.updatePackage(payload).subscribe({
      next: () => this.successMsg("Package updated successfully"),
      error: () => this.errorMsg(),
      complete: () => (this.isLoading = false)
    });
  }

  successMsg(msg: string) {
    this.messageService.add({ severity: "success", summary: "Success", detail: msg });
    //this.reload.emit();
    this.handleClose();
  }

  errorMsg() {
    this.messageService.add({ severity: "error", summary: "Error", detail: "Something went wrong" });
  }

  handleClose() {
    this.packageForm.reset();
    this.packageFeatures = [];
    this.close.emit();
  }

}
